import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight, HelpCircle, BookOpen, Code, Settings, MessageCircle, ArrowLeft } from "lucide-react";
import { useMobile } from "../hooks/useMobile";
import { SEOHead } from "../components/SEOHead";
import { generateFAQSchema, generateBreadcrumbSchema } from "../utils/seo";

interface FAQSection {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  questions: {
    id: string;
    question: string;
    answer: string;
  }[];
}

const faqSections: FAQSection[] = [
  {
    id: "general",
    title: "Общие вопросы",
    icon: HelpCircle,
    questions: [
      {
        id: "what-is",
        question: "Что такое Quantile Camp?",
        answer:
          "Quantile Camp — это практическая платформа по ClickHouse и SQL. Внутри есть интерактивные курсы, тренажёр с задачами, AI Tutor и песочница (Sandbox) для экспериментов. Всё работает прямо в браузере — без установки ClickHouse локально."
      },
      {
        id: "who-for",
        question: "Для кого предназначена платформа?",
        answer:
          "Платформа подойдёт аналитикам, разработчикам, инженерам данных и студентам, которые хотят научиться уверенно писать запросы к ClickHouse. Материалы рассчитаны на начинающих и продолжающих: от первых SELECT до оптимизации запросов и понимания движков."
      },
      {
        id: "free",
        question: "Платформа бесплатная?",
        answer:
          "Да, сейчас Quantile Camp бесплатен: вы можете проходить курс, пользоваться тренажёром, AI Tutor и Sandbox без оплаты. Если в будущем появятся платные программы, базовый доступ к учебным задачам и тренажёру мы постараемся сохранить."
      },
      {
        id: "registration",
        question: "Нужна ли регистрация и зачем Telegram?",
        answer:
          "Вход через Telegram нужен, чтобы сохранять ваш прогресс по задачам и курсам, а также привязывать результаты к одному аккаунту на всех устройствах. Без входа вы можете открыть интерфейс, но прогресс и решения не будут надёжно сохраняться."
      }
    ]
  },
  {
    id: "simulator",
    title: "Работа с тренажёром",
    icon: Code,
    questions: [
      {
        id: "how-to-start",
        question: "Как начать работу с тренажёром?",
        answer:
          "Откройте блок «Практика → Тренажёр» и выберите задачу. На странице задачи слева находится описание, по центру — редактор SQL и результаты, справа — AI Tutor. Пишите запрос в редакторе и используйте кнопки «Запуск» и «Проверить» над ним."
      },
      {
        id: "check-query",
        question: "Как проверить правильность запроса?",
        answer:
          "После того как вы написали запрос, нажмите «Проверить». Мы выполним запрос, сравним результат с эталонным и покажем, прошла ли проверка. При успешной проверке задача помечается как выполненная, а прогресс в тренажёре обновляется."
      },
      {
        id: "run-query",
        question: "В чём разница между «Запуск» и «Проверить»?",
        answer:
          "«Запуск» просто выполняет запрос и показывает таблицу с результатами и статистику (время, read_rows, read_bytes и т.п.). «Проверить» дополнительно сравнивает результат с правильным ответом и учитывает задачу в вашем прогрессе."
      },
      {
        id: "save-progress",
        question: "Как сохраняется прогресс по задачам?",
        answer:
          "Если вы вошли через Telegram, все проверки задач сохраняются автоматически: видно, какие задачи выполнены, какие упали на проверке. Прогресс синхронизируется между тренажёром, курсами и страницей «Практика», чтобы вы могли продолжить с любого устройства."
      },
      {
        id: "schema-docs",
        question: "Где посмотреть структуру таблиц и данные для задач?",
        answer:
          "В тренажёре есть встроенная документация по данным. В редакторе SQL нажмите на иконку схем/таблиц — откроется Data Docs со списком доменов, таблиц и описаниями колонок. Там же можно быстро перейти в Sandbox, чтобы поэкспериментировать с таблицей."
      },
      {
        id: "editor-features",
        question: "Какие есть удобства в редакторе SQL?",
        answer:
          "Редактор поддерживает подсветку синтаксиса, автодополнение таблиц и функций ClickHouse, форматирование запроса, копирование кода, вывод статистики выполнения и отладочный запуск (Debug). Высоту редактора можно менять, а панель результатов — разворачивать."
      },
      {
        id: "sandbox",
        question: "Что такое Sandbox и когда он нужен?",
        answer:
          "Sandbox — отдельная страница и режим без проверки, где вы можете запускать любые запросы к тому же учебному кластеру. В нём удобно проверять гипотезы, разбираться с функциями и повторять примеры из уроков, не привязываясь к конкретной задаче."
      }
    ]
  },
  {
    id: "courses",
    title: "Курсы и уроки",
    icon: BookOpen,
    questions: [
      {
        id: "how-courses-work",
        question: "Как устроен курс по ClickHouse?",
        answer:
          "Курс состоит из модулей, внутри — уроки. Уроки комбинируют теорию, примеры SQL, мини‑задачи и ссылки в тренажёр. Курс можно проходить последовательно или точечно — прогресс по каждому уроку и модулю сохраняется и отображается в левом списке."
      },
      {
        id: "lesson-navigation",
        question: "Как навигироваться по урокам и модулям?",
        answer:
          "На странице курса слева есть список модулей. Нажмите на модуль, чтобы открыть окно с уроками и перейти к нужному. На странице урока этот же список доступен сбоку и показывает статусы: не начат, в процессе, завершён."
      },
      {
        id: "lesson-features",
        question: "Что есть на странице урока кроме текста?",
        answer:
          "Помимо текста вы увидите встроенные SQL‑блоки (демо и практические), квизы, ссылки в тренажёр и AI Tutor, который знает контекст урока. Во многих уроках можно сразу запускать запросы в учебном кластере или быстро перейти в Sandbox с предзаполненным кодом."
      },
      {
        id: "code-blocks",
        question: "Чем отличаются демонстрационные и практические SQL‑блоки?",
        answer:
          "Демо‑блоки показывают готовые запросы и результаты — их можно запускать, но они не влияют на прогресс. Практические блоки привязаны к задаче в тренажёре: вы пишете запрос, запускаете/проверяете его, и успешное решение засчитывается как выполненная задача."
      },
      {
        id: "course-progress",
        question: "Как считать, что модуль или курс пройден?",
        answer:
          "Урок считается завершённым, когда вы дочитали его и выполнили ключевые практические шаги. В карточке модуля и в прогрессе курса видно, сколько уроков завершено и какой общий процент пройден. После 100% курс помечается как завершённый, но вы всегда можете вернуться."
      }
    ]
  },
  {
    id: "ai-tutor",
    title: "AI Tutor и Roadmap",
    icon: MessageCircle,
    questions: [
      {
        id: "what-is-ai-tutor",
        question: "Что такое AI Tutor и чем он помогает?",
        answer:
          "AI Tutor — это встроенный помощник по курсу и тренажёру. Он объясняет теорию, разбирает SQL‑запросы, подсказывает, с какого модуля начать, и помогает не застревать на задачах. На странице курса и урока он открывается справа как отдельная панель."
      },
      {
        id: "how-to-use-ai-tutor",
        question: "Как открыть AI Tutor на разных страницах?",
        answer:
          "На странице курса и урока кнопка AI Tutor находится справа вверху/справа от контента, в тренажёре — отдельная панель справа от редактора. Нажмите на иконку чата, чтобы открыть или скрыть панель. Внутри — предложенные вопросы и быстрые сценарии по теме."
      },
      {
        id: "ai-tutor-features",
        question: "Какие режимы есть у AI Tutor?",
        answer:
          "AI Tutor умеет отвечать на свободные вопросы, предлагать наводящие подсказки, разбирать запрос построчно и давать план обучения. В отдельных режимах он показывает Roadmap по темам курса и помогает выбирать следующий модуль или урок с учётом вашего прогресса."
      },
      {
        id: "ai-tutor-sandbox",
        question: "Как совместить AI Tutor и работу с кодом?",
        answer:
          "Часто удобно держать AI Tutor справа, а слева — урок или тренажёр. Можно копировать запросы из ответов AI в редактор, запускать их в Sandbox или тренажёре и возвращаться за следующими подсказками. История диалогов для курса и тренажёра хранится отдельно."
      }
    ]
  },
  {
    id: "sql-clickhouse",
    title: "SQL и ClickHouse",
    icon: BookOpen,
    questions: [
      {
        id: "clickhouse-sql",
        question: "Чем SQL в ClickHouse отличается от «классического» SQL?",
        answer:
          "ClickHouse поддерживает привычные SELECT / JOIN / GROUP BY, но добавляет аналитику по большим данным: массивы, кортежи, Map, быстрые агрегаты, quantile‑функции, условия в агрегатах, продвинутую работу с датами и временем, а также специфичные движки таблиц."
      },
      {
        id: "functions",
        question: "Как узнать, какие функции ClickHouse доступны в тренажёре?",
        answer:
          "В редакторе включено автодополнение по функциям: начните вводить имя, и появится список с кратким описанием. Большинство функций из официальной документации доступны и в нашем учебном кластере, но мы иногда прячем экспериментальные или нестабильные."
      },
      {
        id: "syntax-help",
        question: "Есть ли подсказки по синтаксису и форматированию?",
        answer:
          "Да. Ключевые слова и функции подсвечиваются, при наведении на многие функции появляется краткая справка, а кнопка «Форматировать» приводит запрос к аккуратному виду. Это удобно перед тем, как отправлять решение на проверку или в AI Tutor."
      },
      {
        id: "errors",
        question: "Что делать, если запрос падает с ошибкой?",
        answer:
          "Сообщение об ошибке показывается под редактором и в блоке результатов. Сначала проверьте синтаксис и имена колонок/таблиц. Если не получается разобраться, откройте Data Docs или спросите AI Tutor, приложив проблемный запрос и текст ошибки."
      },
      {
        id: "data-docs",
        question: "Что такое Data Docs?",
        answer:
          "Data Docs — это встроенная документация по учебным данным: домены, таблицы, описания колонок и типы. Она помогает понять, откуда берутся данные в задачах и как устроены схемы. Открывается из тренажёра и из части уроков по кнопке со значком таблиц."
      },
      {
        id: "query-results",
        question: "Как работать с результатами запросов и графиками?",
        answer:
          "Результаты по умолчанию показываются в таблице, но вы можете переключиться на вкладку «График», выбрать оси и построить визуализацию по числовым колонкам. Табличный результат можно скопировать в Markdown, а под графиком видна статистика выполнения запроса."
      }
    ]
  },
  {
    id: "technical",
    title: "Технические вопросы",
    icon: Settings,
    questions: [
      {
        id: "browser",
        question: "Какие браузеры и устройства поддерживаются?",
        answer:
          "Мы ориентируемся на последние версии Chrome, Firefox, Safari и Edge. Платформа работает и на планшетах, и на ноутбуках; на телефоне интерфейс адаптируется, но писать длинные SQL‑запросы там менее удобно."
      },
      {
        id: "mobile",
        question: "Можно ли проходить курс с телефона?",
        answer:
          "Да, основные страницы и AI Tutor оптимизированы под мобильный экран. Однако для серьёзной работы с тренажёром и Sandbox комфортнее использовать ноутбук или планшет с внешней клавиатурой."
      },
      {
        id: "data",
        question: "Как вы храните мои данные и прогресс?",
        answer:
          "Мы сохраняем только необходимый минимум: ваш Telegram‑ID, прогресс по задачам/урокам и технические события для улучшения платформы. Данные хранятся на наших серверах и не передаются третьим лицам без отдельного согласия."
      },
      {
        id: "bug",
        question: "Куда писать, если что‑то сломалось или есть идея?",
        answer:
          "Самый быстрый способ — написать нам в Telegram боту @quantilecampbot или воспользоваться кнопкой «Сообщить об ошибке» в тренажёре. Пожалуйста, прикладывайте скриншот и краткое описание: так мы быстрее воспроизводим и чиним проблему."
      }
    ]
  }
];

export const FAQ = () => {
  const { sectionId, questionId } = useParams<{ sectionId?: string; questionId?: string }>();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(sectionId ? [sectionId] : [])
  );

  const currentSection = sectionId 
    ? faqSections.find(s => s.id === sectionId)
    : null;

  const currentQuestion = questionId && currentSection
    ? currentSection.questions.find(q => q.id === questionId)
    : null;

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
      navigate("/faq");
    } else {
      newExpanded.add(sectionId);
      navigate(`/faq/${sectionId}`);
    }
    setExpandedSections(newExpanded);
  };

  const handleQuestionClick = (sectionId: string, questionId: string) => {
    navigate(`/faq/${sectionId}/${questionId}`);
  };

  const sectionCards = useMemo(
    () =>
      faqSections.map((section) => {
        const Icon = section.icon;
        const previewQuestions = section.questions.slice(0, 3);
        return (
          <div
            key={section.id}
            className="border border-neutral-border rounded-lg p-5 hover:border-primary/30 transition-colors cursor-pointer flex flex-col gap-3"
            onClick={() => navigate(`/faq/${section.id}`)}
          >
            <div className="flex items-center gap-3">
              <Icon className="w-6 h-6 text-primary" />
              <div className="flex flex-col gap-0.5 min-w-0">
                <h2 className="text-base sm:text-lg font-semibold text-neutral-text">
                  {section.title}
                </h2>
                <p className="text-neutral-text/60 text-xs sm:text-sm">
                  {section.questions.length} вопросов
                </p>
              </div>
            </div>
            <div className="border-t border-neutral-border/60 pt-3 mt-1 space-y-1.5">
              {previewQuestions.map((q) => (
                <div
                  key={q.id}
                  className="flex items-start gap-2 text-xs sm:text-sm text-neutral-text/80"
                >
                  <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-neutral-text/40" />
                  <span className="line-clamp-2">{q.question}</span>
                </div>
              ))}
              {section.questions.length > previewQuestions.length && (
                <div className="text-xs text-neutral-text/50">
                  Ещё {section.questions.length - previewQuestions.length}…
                </div>
              )}
            </div>
          </div>
        );
      }),
    [navigate]
  );

  // Собираем все FAQ для структурированных данных
  const allFAQs = useMemo(() => {
    return faqSections.flatMap(section => 
      section.questions.map(q => ({
        question: q.question,
        answer: q.answer
      }))
    );
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="FAQ — Часто задаваемые вопросы | Quantile Camp"
        description="Ответы на часто задаваемые вопросы о Quantile Camp: как начать обучение, работа с тренажёром, курсы ClickHouse, регистрация и прогресс."
        keywords="FAQ Quantile Camp, вопросы о ClickHouse, помощь по SQL, как начать обучение ClickHouse, регистрация Quantile Camp, работа с тренажёром, ClickHouse помощь, SQL обучение вопросы, Quantile Camp инструкция, ClickHouse для начинающих FAQ, SQL тренажёр помощь, обучение ClickHouse с нуля"
        url="https://www.quantilecamp.ru/faq"
        structuredData={[
          generateFAQSchema(allFAQs),
          generateBreadcrumbSchema([
            { name: "Главная", url: "/" },
            { name: "FAQ", url: "/faq" }
          ])
        ]}
      />
      <div className={`max-w-7xl mx-auto ${isMobile ? "px-4 pt-0 pb-4" : "flex"}`}>
        {/* Боковая навигация */}
        {!isMobile && (
          <aside className="w-64 flex-shrink-0 border-r border-neutral-border bg-neutral-light/30 h-screen sticky top-[var(--header-height,4.5rem)] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-6">
                <MessageCircle className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-neutral-text">FAQ</h2>
              </div>
              
              <nav className="space-y-1">
                {faqSections.map((section) => {
                  const Icon = section.icon;
                  const isExpanded = expandedSections.has(section.id);
                  const isActive = sectionId === section.id;

                  return (
                    <div key={section.id}>
                      <button
                        onClick={() => toggleSection(section.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-neutral-text hover:bg-neutral-light"
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1 text-left">{section.title}</span>
                        <ChevronRight
                          className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                        />
                      </button>
                      
                      {isExpanded && (
                        <div className="ml-6 mt-1 space-y-1">
                          {section.questions.map((question) => {
                            const isQuestionActive = questionId === question.id && sectionId === section.id;
                            return (
                              <button
                                key={question.id}
                                onClick={() => handleQuestionClick(section.id, question.id)}
                                className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${
                                  isQuestionActive
                                    ? "text-primary font-medium bg-primary/5"
                                    : "text-neutral-text/70 hover:text-neutral-text hover:bg-neutral-light/50"
                                }`}
                              >
                                {question.question}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </aside>
        )}

        {/* Основной контент */}
        <main className="flex-1 min-w-0">
          <div className={`max-w-4xl mx-auto ${isMobile ? "py-2" : "pt-8 px-8 pb-8"}`}>
            {isMobile && (
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-neutral-text">FAQ</h2>
                </div>
                {sectionId && (
                  <button
                    onClick={() => navigate("/faq")}
                    className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark"
                  >
                    <ArrowLeft size={16} />
                    Назад
                  </button>
                )}
              </div>
            )}

            {currentQuestion ? (
              // Страница конкретного вопроса
              <article>
                <nav className="mb-6 text-sm text-neutral-text/60 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => navigate(`/faq/${sectionId}`)}
                    className="hover:text-primary transition-colors"
                  >
                    {currentSection?.title}
                  </button>
                  <span className="mx-1">/</span>
                  <span>{currentQuestion.question}</span>
                </nav>
                
                <h1 className="text-3xl font-bold text-neutral-text mb-6">
                  {currentQuestion.question}
                </h1>
                
                <div className="prose prose-sm max-w-none">
                  <p className="text-neutral-text/80 leading-relaxed text-base">
                    {currentQuestion.answer}
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-neutral-border">
                  <button
                    onClick={() => navigate("/faq")}
                    className="text-primary hover:text-primary-dark transition-colors text-sm font-medium"
                  >
                    ← Вернуться к FAQ
                  </button>
                </div>
              </article>
            ) : currentSection ? (
              // Страница раздела
              <article>
                <nav className="mb-6 text-sm text-neutral-text/60 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => navigate("/faq")}
                    className="hover:text-primary transition-colors"
                  >
                    FAQ
                  </button>
                  <span className="mx-1">/</span>
                  <span>{currentSection.title}</span>
                </nav>
                
                <div className="flex items-center gap-3 mb-6">
                  {(() => {
                    const Icon = currentSection.icon;
                    return <Icon className="w-8 h-8 text-primary" />;
                  })()}
                  <h1 className="text-3xl font-bold text-neutral-text">
                    {currentSection.title}
                  </h1>
                </div>
                
                <div className="space-y-4">
                  {currentSection.questions.map((question) => (
                    <div
                      key={question.id}
                      className="border border-neutral-border rounded-lg p-4 bg-white"
                    >
                      <h2 className="text-lg font-semibold text-neutral-text mb-2">
                        {question.question}
                      </h2>
                      <p className="text-neutral-text/80 text-sm leading-relaxed">
                        {question.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </article>
            ) : (
              // Главная страница FAQ
              <article>
                <div className="mb-8">
                  <h1 className="text-4xl font-bold text-neutral-text mb-4">
                    Часто задаваемые вопросы
                  </h1>
                  <p className="text-lg text-neutral-text/70">
                    Найдите ответы на популярные вопросы о Quantile Camp
                  </p>
                </div>

                {isMobile ? (
                  <div className="space-y-3">
                    {sectionCards}
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {sectionCards}
                  </div>
                )}
              </article>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

